import AkLegacyButton from '@atlaskit/button';
import Button from '@atlaskit/button/new';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import { CSSObjectWithLabel } from '@atlaskit/react-select';
import { PopupSelect } from '@atlaskit/select';
import React from 'react';

import { useIsHomeVisualRefreshEnabled } from '@townsquare/stat-sig/nav4';

import { Operators } from '../../types';
import { GROUP_OPERATOR_CLASSNAME, POPPER_MODIFIERS } from '../style';

interface OperatorPickerOption {
  label: string;
  value: Operators;
}

interface OperatorPickerPopupProps {
  isRootOperator: boolean;
  options: OperatorPickerOption[];
  selected: Operators;
  onChange: (operator: Operators) => void;
  onClose: () => void;
  buttonStyle: React.CSSProperties;
}

const getClassName = (isOpen: boolean, root?: boolean) => {
  if (isOpen) {
    return undefined;
  }

  return Object.entries({ root, [GROUP_OPERATOR_CLASSNAME]: true })
    .map(([key, value]) => {
      if (value) {
        return key;
      }
    })
    .filter(v => !!v)
    .join('-');
};

const OperatorPickerPopup = (props: OperatorPickerPopupProps) => {
  const isHomeVisualRefreshEnabled = useIsHomeVisualRefreshEnabled();
  return (
    <PopupSelect<OperatorPickerOption>
      options={props.options}
      onChange={item => {
        if (item?.value) {
          props.onChange(item.value);
        }
      }}
      onClose={props.onClose}
      minMenuWidth={80}
      maxMenuWidth={80}
      value={props.options.find(option => option.value === props.selected)}
      formatOptionLabel={(option, { context }) =>
        context === 'value' ? (
          option.label
        ) : (
          <span data-testid={`operator-picker-option-${option.value}`}>{option.label}</span>
        )
      }
      styles={{
        option: (css, optionProps) => {
          return {
            ...css,
            cursor: optionProps.isDisabled ? 'not-allowed' : 'pointer',
          } as CSSObjectWithLabel;
        },
      }}
      target={({ ref, isOpen }) =>
        isHomeVisualRefreshEnabled ? (
          <Button
            ref={ref}
            iconAfter={ChevronDownIcon}
            isSelected={isOpen}
            spacing={props.isRootOperator ? 'default' : 'compact'}
            appearance="subtle"
            testId={`metafilter-${props.isRootOperator ? 'root-' : ''}operator-picker`}
          >
            {props.options.find(item => item.value === props.selected)?.label}
          </Button>
        ) : (
          <AkLegacyButton
            ref={ref}
            spacing="compact"
            appearance="subtle"
            isSelected={isOpen}
            // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
            style={props.buttonStyle}
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
            className={getClassName(isOpen, props.isRootOperator)}
            iconAfter={<ChevronDownIcon color="currentColor" LEGACY_size="medium" label="" />}
            testId={`metafilter-${props.isRootOperator ? 'root-' : ''}operator-picker`}
          >
            {props.options.find(item => item.value === props.selected)?.label}
          </AkLegacyButton>
        )
      }
      popperProps={POPPER_MODIFIERS}
    />
  );
};

export default OperatorPickerPopup;
